import React, { useMemo } from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Phone } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { LEGAL_PAGES } from "../data/legalPages";
import SEO from "../components/SEO";

export default function LegalPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+/, "").split("/")[0];
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const data = LEGAL_PAGES[slug];

  const t = useMemo(() => ({
    back: isAr ? "← العودة" : "← Back",
    updated: isAr ? "آخر تحديث" : "Last updated",
    needHelp: isAr ? "تحتاج إلى مساعدة قانونية مباشرة؟" : "Need direct legal help?",
    cta: isAr ? "احجز موعداً مع محامٍ" : "Book an appointment",
    contact: isAr ? "تواصل مع المحاسبة" : "Contact billing",
  }), [isAr]);

  if (!data) return <Navigate to="/" replace />;

  const title = isAr ? data.titleAr : data.titleEn;
  const lead = isAr ? data.leadAr : data.leadEn;
  const eyebrow = isAr ? data.eyebrowAr : data.eyebrowEn;
  const updated = isAr ? data.updatedAr : data.updatedEn;
  const sections = isAr ? data.sectionsAr : data.sectionsEn;

  return (
    <div data-testid={`legal-page-${slug}`} className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <SEO
        titleAr={`${data.titleAr} — مجموعة الدخيل القانونية`}
        titleEn={`${data.titleEn} — Aldakheel Legal Group`}
        descriptionAr={data.leadAr}
        descriptionEn={data.leadEn}
      />

      <Link
        to="/"
        data-testid="legal-back-home"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 transition-colors mb-10"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t.back}
      </Link>

      <div className="border-b pb-10 mb-12" style={{ borderColor: "var(--line)" }}>
        <p className="eyebrow inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--gold-deep)" }} />
          {eyebrow}
        </p>
        <h1
          dir="auto"
          className="font-display text-4xl lg:text-6xl mt-4 leading-[1.0] tracking-tight"
        >
          {title}
        </h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">{lead}</p>
        <p className="mt-5 text-xs uppercase tracking-[0.2em] text-slate-500">
          {t.updated}: <span className="text-slate-700">{updated}</span>
        </p>
      </div>

      <article className="space-y-10" dir={isAr ? "rtl" : "ltr"}>
        {sections.map((s, i) => (
          <section key={i} data-testid={`legal-section-${i}`}>
            <h2 className="font-display text-2xl lg:text-3xl tracking-tight mb-3">
              {s.h}
            </h2>
            <p className="text-base lg:text-[17px] leading-[1.9] text-slate-700">
              {s.p}
            </p>
          </section>
        ))}
      </article>

      {/* CTA card */}
      <div
        className="mt-16 p-7 border-y flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        style={{ borderColor: "var(--line)" }}
      >
        <div>
          <p className="eyebrow">{t.needHelp}</p>
          <p className="font-display text-2xl mt-1">
            {isAr ? "تواصل مع المكتب مباشرة." : "Talk to a lawyer directly."}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href={`https://wa.me/96569656963?text=${encodeURIComponent(isAr ? "مرحباً، أرغب في حجز موعد مع محامٍ." : "Hello, I'd like to book an appointment with a lawyer.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="legal-cta-book"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-sm text-sm btn-gold"
          >
            {t.cta}
          </a>
          <a
            href="https://wa.me/96569656963"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="legal-cta-whatsapp"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-sm text-sm"
            style={{ background: "var(--wa)", color: "#fff" }}
          >
            <Phone className="w-4 h-4" />
            {t.contact}
          </a>
        </div>
      </div>
    </div>
  );
}
